import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Palmtree, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeaveManagement() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [sourceYear, setSourceYear] = useState(currentYear - 1);
  const [targetYear, setTargetYear] = useState(currentYear);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const handleCarryover = async () => {
    if (!window.confirm(`Transférer les jours restants de ${sourceYear} vers le reliquat ${targetYear} pour tous les utilisateurs ?`)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leave-admin/carryover`, { source_year: sourceYear, target_year: targetYear });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Réaffecter les quotas par défaut pour ${selectedYear} à tous les utilisateurs ?`)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leave-admin/reset-quotas`, { year: selectedYear });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Palmtree className="h-5 w-5" />
          {t('leaveManagement')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('carryoverDescription')}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Carryover */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold">{t('carryoverTitle')}</h3>
          <p className="text-sm text-muted-foreground">
            Transférer les jours de congés restants de l'année source vers le reliquat de l'année cible.
          </p>
          <div className="flex gap-3 items-end">
            <div>
              <Label className="text-xs">{t('sourceYear')}</Label>
              <Select value={String(sourceYear)} onValueChange={v => setSourceYear(Number(v))}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <span className="pb-2 text-xl">→</span>
            <div>
              <Label className="text-xs">{t('targetYear')}</Label>
              <Select value={String(targetYear)} onValueChange={v => setTargetYear(Number(v))}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleCarryover} className="w-full" disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('carryoverButton')}
          </Button>
        </div>

        {/* Reset Quotas */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold">{t('resetQuotasTitle')}</h3>
          <p className="text-sm text-muted-foreground">
            Créer ou mettre à jour les allocations pour tous les utilisateurs selon leurs jours par défaut.
          </p>
          <div>
            <Label className="text-xs">Année cible</Label>
            <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleReset} variant="secondary" className="w-full" disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('resetQuotasButton')} {selectedYear}
          </Button>
        </div>
      </div>

      <div className="pt-4 border-t">
        <a href="/leave" className="text-primary hover:underline">→ Voir les soldes de congés</a>
      </div>
    </div>
  );
}
